import { useEffect, useRef } from 'react';
import {
  WheelResolveScene,
  type WheelResolveSceneCallbacks,
} from '@goodtime/engine';
import type { BotArchetype, ItemId, WheelObservableState } from '@goodtime/shared';
import wheelHubUrl from '../../assets/wheel/hub.png';
import wheelOuterRimUrl from '../../assets/wheel/outer_rim.png';
import wheelPointerUrl from '../../assets/wheel/pointer.png';
import coinIconUrl from '../../assets/icon/coin.png';
import highFiveUrl from '../../assets/gesture/high_five.png';
import { submitWheelCommand } from '../../api/wheelCommand';
import { usePlayerAvatar } from '../../avatar/usePlayerAvatar';
import { usePlayerCustomAvatar } from '../../avatar/usePlayerCustomAvatar';
import { resolveAvatarUrl } from './avatarResolver';
import { rememberSentGesture } from './gestureMemory';
import { WHEEL_ITEM_ICON_URLS } from './WheelResolveView';

/**
 * Pixi mount for the multiplayer wheel-resolve view. Producer brief
 * 2026-05-16 — replaces the legacy CSS-in-React {@code WheelStage}
 * component (disc + hub + rim + perimeter avatars) with a Pixi
 * canvas. The surrounding chrome (banner, deploy strip, action
 * cards, outcome chips) is still React for now and sits OVER this
 * canvas via z-index; subsequent phases of the port lift those
 * into Pixi too.
 *
 * <p>The component is purely controlled — every prop change
 * triggers a {@code scene.setState(...)} push. The scene compares
 * against its prior tick internally so we don't re-render the
 * world tree per state change.
 */

export interface WheelResolveCanvasProps {
  state: WheelObservableState;
  selfSeatId: number | null;
  /** Room id needed for SELECT_SEAT command submission. Null when
   *  the canvas is rendered in a dev-preview fixture without a
   *  live room (the seat-tap callback no-ops in that case). */
  roomId: string | null;
  playerId: string | null;
  /** U11 (2026-05-30) — when set, tapping a rim avatar OUTSIDE the
   *  STAKE phase invokes this callback with the seat's archetype so
   *  the parent can open a {@code ProfileCard}. STAKE-phase taps stay
   *  on SELECT_SEAT (swap into that slot) per the design call: the
   *  rim is a positioning surface during STAKE and a spectating
   *  surface during WHEEL_RESOLVE, so the same gesture means different
   *  things by phase. Player seats (archetype === null) are skipped.
   *  Pass {@code null} to disable the affordance (e.g. audience). */
  onArchetypeTap?: ((archetype: BotArchetype) => void) | null;
}

export function WheelResolveCanvas({
  state,
  selfSeatId,
  roomId,
  playerId,
  onArchetypeTap = null,
}: WheelResolveCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<WheelResolveScene | null>(null);
  const [playerAvatarId] = usePlayerAvatar();
  const [customAvatar] = usePlayerCustomAvatar();

  // Latest props captured for the scene's onSeatTap callback — the
  // scene is constructed once but the callback needs to read the
  // current state.phase, roomId, playerId, and onArchetypeTap to
  // decide what to do per tap. Without this ref the closure would
  // capture the initial values forever.
  const tapDispatchRef = useRef<{
    state: WheelObservableState;
    roomId: string | null;
    playerId: string | null;
    selfSeatId: number | null;
    onArchetypeTap: ((archetype: BotArchetype) => void) | null;
  }>({
    state,
    roomId,
    playerId,
    selfSeatId,
    onArchetypeTap,
  });
  tapDispatchRef.current = {
    state,
    roomId,
    playerId,
    selfSeatId,
    onArchetypeTap,
  };

  // Construct the scene once per mount. Avatar URLs are pushed
  // separately on every state change so the scene can load them
  // via Pixi.Assets internally (keeps Pixi types out of the
  // mobile app's imports — only @goodtime/engine sees pixi.js).
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const callbacks: WheelResolveSceneCallbacks = {
      onSeatTap: (slot) => {
        // Dispatch by phase via the ref so we always read the freshest
        // state. STAKE → SELECT_SEAT swap (existing behaviour).
        // WHEEL_REVEAL / WHEEL_RESOLVE → open the ProfileCard for the
        // archetype at that slot. PAYOUT shouldn't be hit (the canvas
        // unmounts on PAYOUT entry) but the archetype branch covers
        // it harmlessly if the timing slips by a frame.
        const d = tapDispatchRef.current;
        if (d.state.phase === 'STAKE') {
          if (!d.roomId || !d.playerId) return;
          void submitWheelCommand(d.roomId, 'SELECT_SEAT', d.playerId, { slot })
            .catch((e) => console.warn('[WheelResolveCanvas] SELECT_SEAT failed', e));
          return;
        }
        if (!d.onArchetypeTap) return;
        // Look up the seat occupying this slot. Skip player seats
        // (archetype: null) and own seat — neither has a profile to
        // show within U11 scope.
        const occupantEntry = Object.entries(d.state.seatPointerSlots ?? {}).find(
          ([, s]) => Number(s) === slot,
        );
        if (!occupantEntry) return;
        const occupantSeatId = Number(occupantEntry[0]);
        if (d.selfSeatId !== null && occupantSeatId === d.selfSeatId) return;
        const occupantSeat = d.state.seats.find((s) => s.seatId === occupantSeatId);
        if (!occupantSeat || occupantSeat.archetype === null) return;
        d.onArchetypeTap(occupantSeat.archetype as BotArchetype);
      },
      onDeployItem: (itemId) => {
        // Read room/player off the ref, NOT the closure: the scene is
        // built once (deps []), so a closure capture of roomId/playerId
        // can be stale/null from mount — which silently dropped every
        // DEPLOY_ITEM ("tiles show, tap does nothing, it just spins").
        // onSeatTap already does this; this callback was missed.
        const d = tapDispatchRef.current;
        if (!d.roomId || !d.playerId) return;
        void submitWheelCommand(d.roomId, 'DEPLOY_ITEM', d.playerId, { itemId })
          .catch((e) =>
            console.warn('[WheelResolveCanvas] DEPLOY_ITEM failed', e),
          );
      },
      onSeatGesture: (slot) => {
        // High-five badge tap (producer 2026-06-08). Resolve the
        // occupant at this slot and fire a directed REACTION at them.
        // Works for any non-self occupant (bot OR human). Read the
        // freshest props off the ref so the closure isn't stale.
        // rememberSentGesture makes the reciprocation read as "...back".
        const d = tapDispatchRef.current;
        if (!d.roomId || !d.playerId) return;
        const occupantEntry = Object.entries(d.state.seatPointerSlots ?? {}).find(
          ([, s]) => Number(s) === slot,
        );
        if (!occupantEntry) return;
        const targetSeatId = Number(occupantEntry[0]);
        if (d.selfSeatId !== null && targetSeatId === d.selfSeatId) return;
        rememberSentGesture(targetSeatId, 'high_five');
        void submitWheelCommand(d.roomId, 'REACTION', d.playerId, {
          reactionId: 'high_five',
          targetSeatId,
        }).catch((e) =>
          console.warn('[WheelResolveCanvas] high-five failed', e),
        );
      },
    };
    const scene = new WheelResolveScene(
      container,
      {
        hubTextureUrl: wheelHubUrl,
        rimTextureUrl: wheelOuterRimUrl,
        pointerTextureUrl: wheelPointerUrl,
        coinTextureUrl: coinIconUrl,
        highFiveTextureUrl: highFiveUrl,
      },
      callbacks,
    );
    sceneRef.current = scene;
    // Debug: expose the scene globally so the preview harness can
    // poke at internal state without instrumenting React.
    (window as unknown as { __wheelScene?: unknown }).__wheelScene = scene;
    // Push the item-icon URL map immediately — they're static
    // (every PNG in /assets/item/) so we only need to do this
    // once per mount.
    const iconUrls = new Map<ItemId, string>();
    for (const [id, url] of Object.entries(WHEEL_ITEM_ICON_URLS)) {
      if (url) iconUrls.set(id as ItemId, url);
    }
    scene.setItemIconUrls(iconUrls);
    void scene.init().catch((err) => {
      console.error('[WheelResolveCanvas] scene init failed', err);
    });
    return () => {
      sceneRef.current = null;
      scene.destroy();
    };
    // Initialise once — roomId / playerId / avatarId changes are
    // forwarded through the next effect's setState push rather
    // than re-creating the scene.
  }, []);

  // Resolve avatar URLs for every seat + push the latest state.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const urls = new Map<number, string>();
    for (const seat of state.seats) {
      // The local player's own seat shows their custom upload when they have
      // one; every other seat resolves as before (bot art / default face).
      const url = seat.seatId === selfSeatId && customAvatar?.url
        ? customAvatar.url
        : resolveAvatarUrl(seat, playerAvatarId);
      if (url) urls.set(seat.seatId, url);
    }
    scene.setAvatarUrls(urls, selfSeatId);
    scene.setState(state, selfSeatId);
  }, [state, selfSeatId, playerAvatarId, customAvatar?.url]);

  return (
    <div
      ref={containerRef}
      data-wheel-resolve-canvas="true"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        pointerEvents: 'auto',
      }}
    />
  );
}
